import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const HIGHLIGHT_PARAM = 'highlight';
const FLASH_MS = 2000;

/**
 * Deep-link highlight: when the URL carries `?highlight=<id>`, scrolls the returned
 * ref into view and briefly flashes it (e.g. arriving from the config-health banner's
 * "Fix" link). Mirrors the shipments `?shipment=` deep-link pattern.
 *
 * The param is intentionally left in the URL: the effect keys off whether it matches
 * this `id`, so navigating to a different `highlight` value re-triggers, while staying
 * on the page does not. Pair the returned `isHighlighted` with a ring utility.
 */
export function useHighlightSection<T extends HTMLElement = HTMLDivElement>(id: string) {
  const [searchParams] = useSearchParams();
  const ref = useRef<T>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const isTarget = searchParams.get(HIGHLIGHT_PARAM) === id;

  useEffect(() => {
    if (!isTarget) return;

    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setIsHighlighted(true);
    const timer = setTimeout(() => setIsHighlighted(false), FLASH_MS);

    return () => clearTimeout(timer);
  }, [isTarget]);

  return { ref, isHighlighted };
}

/** Ring utility applied to a section while it's flashing. */
export const HIGHLIGHT_RING = 'ring-2 ring-primary ring-offset-2 transition-shadow';
